import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { useScale, ScaleFn, useDarkMode } from './common';
import Data from './Data';

/// 감도 자동조정 — 센서 관리 화면의 한 탭.
///
/// 지난 사이클에서 "교체할 만큼 젖었는데 알림 임계를 못 넘은" 횟수(미알림)를 세어
/// 그걸 줄이는 DIV / W1·W2 를 추천한다. 계산은 서버가 한다(adminserver/sensor_tune.js).
///
/// ★추천은 자동으로 적용되지 않는다. 잘못 둔감해지면 **알림이 조용해지는 쪽으로 실패**해서
///   사고로 이어진다. 반드시 사람이 승인하고, 되돌릴 수 있어야 한다.
/// ★코드(기저귀 종류) 기본값을 먼저 맞추고, 그래도 남는 사람만 개인 조정을 쓴다 —
///   충주 실측에서 코드 4개만 내려도 개선의 대부분(66% / 개인별 74%)을 가져갔다.
const SensorTune = ({ T, S }: any) => {
    // ★useScale() 은 {scale, s} 를, useDarkMode() 는 {dark, setDark} 를 돌려준다.
    //   통째로 받아 s(12) 로 쓰면 "s is not a function" 으로 화면이 죽는다.
    const { s } = useScale();
    const { dark } = useDarkMode();
    const [rows, setRows] = useState<any[]>([]);
    const [hist, setHist] = useState<any[]>([]);
    const [sel, setSel] = useState<Record<string, boolean>>({});
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<string>('');
    const [days, setDays] = useState(14);
    const [testDays, setTestDays] = useState(7);
    const [leadMax, setLeadMax] = useState(90);

    const load = useCallback(() => {
        Data.fetchData('sensor/getTuneSuggest', {}, (r: any) => setRows(r || []), true);
        Data.fetchData('sensor/getTuneHistory', {}, (r: any) => setHist(r || []), true);
    }, []);
    useEffect(() => { load(); }, [load]);

    const run = () => {
        setBusy(true);
        setMsg('계산을 시작했습니다. 3~5분 걸립니다 — 끝나면 목록이 채워집니다.');
        Data.fetchData('sensor/runTuneAnalysis',
            { DAYS: days, TEST_DAYS: testDays, LEAD_MAX: leadMax },
            () => {
                // 계산은 서버가 백그라운드로 돈다. 30초마다 다시 읽어 본다.
                let n = 0;
                const t = setInterval(() => {
                    n++; load();
                    if (n >= 12) { clearInterval(t); setBusy(false); }
                }, 30000);
            }, true);
    };

    const codes = rows.filter(r => r.KIND === 'code');
    const sensors = rows.filter(r => r.KIND === 'sensor');
    const keyOf = (r: any) => `${r.KIND}:${r.TARGET}`;
    const picked = rows.filter(r => sel[keyOf(r)] && r.STATUS !== 'applied');

    const apply = () => {
        if (!picked.length) { setMsg('적용할 항목을 선택해 주세요.'); return; }
        if (!window.confirm(`${picked.length}건을 적용합니다.\n\n감지 서버가 30초 안에 새 값을 읽습니다(중계기·센서는 그대로입니다).\n되돌리기는 아래 「변경 이력」에서 할 수 있습니다.`)) return;
        setBusy(true);
        Data.fetchData('sensor/applyTune',
            { ITEMS: picked.map(r => ({ KIND: r.KIND, TARGET: r.TARGET })) },
            (_r: any, _c: any, all: any) => {
                setMsg(all?.message || '적용했습니다.');
                setSel({}); setBusy(false); load();
            }, true);
    };

    const revert = (h: any) => {
        if (!window.confirm(`${h.NAME || h.TARGET} 을(를) 적용 전으로 되돌립니다.\n\n${h.NOTE || ''}`)) return;
        setBusy(true);
        Data.fetchData('sensor/revertTune', { IDXS: [h.IDX] },
            (_r: any, _c: any, all: any) => { setMsg(all?.message || '되돌렸습니다.'); setBusy(false); load(); }, true);
    };

    // ── 작은 조각들 ──────────────────────────────────────────
    const Chip = ({ on, label, onPress }: any) => (
        <TouchableOpacity onPress={onPress}
            style={{ paddingHorizontal: s(12), paddingVertical: s(6), borderRadius: s(16),
                     borderWidth: 1, borderColor: on ? S.accent : T.border,
                     backgroundColor: on ? S.accent + '18' : 'transparent' }}>
            <Text style={{ fontSize: s(12.5), fontWeight: '700', color: on ? S.accent : T.textSub }}>{label}</Text>
        </TouchableOpacity>
    );
    const Btn = ({ label, onPress, kind = 'main', disabled }: any) => (
        <TouchableOpacity onPress={disabled ? undefined : onPress}
            style={{ paddingHorizontal: s(16), paddingVertical: s(9), borderRadius: s(8),
                     backgroundColor: disabled ? T.border : (kind === 'main' ? S.accent : 'transparent'),
                     borderWidth: kind === 'main' ? 0 : 1, borderColor: T.border, opacity: disabled ? 0.6 : 1 }}>
            <Text style={{ fontSize: s(13), fontWeight: '700',
                           color: kind === 'main' ? '#fff' : T.textSub }}>{label}</Text>
        </TouchableOpacity>
    );
    const Th = ({ children, w }: any) => (
        <Text style={{ width: w, fontSize: s(11.5), fontWeight: '700', color: T.textSub }}>{children}</Text>
    );
    const Td = ({ children, w, bold, color }: any) => (
        <Text style={{ width: w, fontSize: s(12.5), fontWeight: bold ? '700' : '400',
                       color: color || T.text }}>{children}</Text>
    );
    const Check = ({ on, onPress }: any) => (
        <TouchableOpacity onPress={onPress}
            style={{ width: s(18), height: s(18), borderRadius: s(4), borderWidth: 1.4,
                     borderColor: on ? S.accent : T.border, backgroundColor: on ? S.accent : 'transparent',
                     alignItems: 'center', justifyContent: 'center' }}>
            {on && <Text style={{ color: '#fff', fontSize: s(12), fontWeight: '900' }}>✓</Text>}
        </TouchableOpacity>
    );
    const gain = (r: any) => (r.MISS_CUR || 0) - (r.MISS_NEW || 0);

    /// 이 추천을 받아들일 만한가 — 숫자만 보고는 판단이 안 되므로 한 줄로 답한다.
    /// ★표본이 적으면(검증 사이클 5개 미만) 1건 차이가 우연이기 쉬워 판단을 보류한다.
    /// ★미알림이 안 줄면서 알림만 일러지는 건 **손해**다. 그걸 분명히 말한다.
    const verdict = (r: any) => {
        const g = gain(r), cyc = r.CYCLES || 0;
        const lc = r.LEAD_CUR, ln = r.LEAD_NEW;
        const leadBad = ln != null && (ln > 90 || (lc != null && lc > 0 && ln > lc * 2.5));
        if (cyc < 5)
            return { k: 'thin', label: '표본 부족', color: T.muted,
                     why: `최근 ${cyc}번만 교체해서 판단하기 이릅니다. 기록이 더 쌓인 뒤에 보세요.` };
        if (g <= 0 && leadBad)
            return { k: 'bad', label: '권하지 않음', color: S.err,
                     why: `놓친 알림이 없는데 알림만 ${lc}분 → ${ln}분 전으로 당겨집니다. 가봐야 별로 안 젖어 있습니다.` };
        if (g <= 0)
            return { k: 'none', label: '효과 없음', color: T.muted,
                     why: '바꿔도 놓치는 알림이 줄지 않습니다. 그대로 두는 편이 낫습니다.' };
        if (leadBad)
            return { k: 'warn', label: '주의', color: S.warn,
                     why: `놓친 알림은 ${g}번 줄지만, 알림이 ${ln}분 전으로 당겨집니다. 너무 자주 울릴 수 있습니다.` };
        return { k: 'good', label: '권함', color: S.ok,
                 why: `${cyc}번 중 ${r.MISS_CUR}번은 알림 없이 교체됐습니다. 바꾸면 ${r.MISS_NEW}번으로 줄어듭니다.` };
    };

    const Badge = ({ v }: any) => (
        <View style={{ paddingHorizontal: s(7), paddingVertical: s(2), borderRadius: s(10),
                       backgroundColor: v.color + '22', borderWidth: 1, borderColor: v.color + '55' }}>
            <Text style={{ fontSize: s(10.5), fontWeight: '800', color: v.color }}>{v.label}</Text>
        </View>
    );

    const Section = ({ title, desc, children }: any) => (
        <View style={{ marginTop: s(18) }}>
            <Text style={{ fontSize: s(14.5), fontWeight: '800', color: T.text }}>{title}</Text>
            {!!desc && <Text style={{ fontSize: s(12), color: T.muted, marginTop: s(3) }}>{desc}</Text>}
            <View style={{ marginTop: s(8), borderWidth: 1, borderColor: T.border, borderRadius: s(10), overflow: 'hidden' }}>
                {children}
            </View>
        </View>
    );

    /// 같은 코드를 쓰는 사람들의 개인 권장값이 얼마나 흩어지는지.
    /// ★흩어짐이 크면 코드 하나로는 못 맞춘다 — 개인 튜닝으로 가야 한다.
    ///   (성심 D4 는 42~101 로 2.4배 차이, 충주 D13 은 좁아서 코드로 해결됐다)
    const spreadOf = (code: string) => {
        const v = sensors.filter(r => r.DIAPER_CODE === code && r.NEW_DIV).map(r => Number(r.NEW_DIV));
        if (v.length < 2) return null;
        const lo = Math.min(...v), hi = Math.max(...v);
        return { lo, hi, n: v.length, ratio: Math.round(hi / Math.max(lo, 1) * 10) / 10 };
    };

    const last = rows.length ? rows[0] : null;

    return (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: s(16), paddingBottom: s(40) }}>
            {/* 설정 */}
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: s(8) }}>
                <Text style={{ fontSize: s(12.5), color: T.textSub, marginRight: s(4) }}>분석 기간</Text>
                {[7, 14, 30].map(d => <Chip key={d} on={days === d} label={`${d}일`} onPress={() => setDays(d)} />)}
                <Text style={{ fontSize: s(12.5), color: T.textSub, marginLeft: s(10), marginRight: s(4) }}>검증</Text>
                {[4, 7].map(d => <Chip key={d} on={testDays === d} label={`${d}일`} onPress={() => setTestDays(d)} />)}
                <Text style={{ fontSize: s(12.5), color: T.textSub, marginLeft: s(10), marginRight: s(4) }}>알림 리드 상한</Text>
                {[60, 90, 120].map(d => <Chip key={d} on={leadMax === d} label={`${d}분`} onPress={() => setLeadMax(d)} />)}
                <View style={{ width: s(10) }} />
                <Btn label={busy ? '계산 중…' : '계산 시작'} onPress={run} disabled={busy} />
                {busy && <ActivityIndicator size="small" color={S.accent} />}
            </View>

            <Text style={{ fontSize: s(11.5), color: T.muted, marginTop: s(8), lineHeight: s(17) }}>
                앞 {days - testDays}일로 값을 고르고 뒤 {testDays}일로 채점합니다. 고를 때 뒤 {testDays}일은 보지 않습니다 —
                같은 데이터로 채점하면 실제보다 좋게 나옵니다.
                {'\n'}미알림 = 교체할 만큼 젖었는데 알림 임계를 한 번도 못 넘은 사이클.
                {'\n'}리드 = 알림이 울린 뒤 실제 교체까지 걸린 시간. 값을 너무 내리면 아직 덜 젖었는데 울려 헛걸음이 되므로 상한을 둡니다.
                {'\n'}★이 상한이 결과를 가장 크게 가릅니다 — 참사랑 14일 실측으로 60분은 5명만 바뀌고(미알림 37→28), 90분은 11명(37→15), 120분은 너무 헐거워져 오히려 나빠졌습니다(37→17).
            </Text>

            <View style={{ marginTop: s(12), padding: s(12), borderRadius: s(8),
                           backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }}>
                <Text style={{ fontSize: s(12.5), fontWeight: '800', color: T.text, marginBottom: s(6) }}>읽는 법</Text>
                <Text style={{ fontSize: s(11.5), color: T.textSub, lineHeight: s(19) }}>
                    · <Text style={{ fontWeight: '700' }}>미알림</Text> — 교체할 만큼 젖었는데 알림이 한 번도 안 울린 횟수입니다.
                    「1 → 0 / 4」는 <Text style={{ fontWeight: '700' }}>4번 교체 중 1번을 놓쳤는데 바꾸면 0번</Text>이 된다는 뜻입니다.{'\n'}
                    · <Text style={{ fontWeight: '700' }}>DIV</Text> — 감도를 정하는 나눗셈 값입니다. <Text style={{ fontWeight: '700' }}>작을수록 민감</Text>해져 더 일찍 울립니다.{'\n'}
                    · <Text style={{ fontWeight: '700' }}>알림 리드</Text> — 알림이 울린 뒤 실제 교체까지 걸린 시간입니다.
                    짧으면 이미 많이 젖은 뒤에 울리는 것이고, <Text style={{ fontWeight: '700' }}>너무 길면 가봐야 할 게 없어 무시하게 됩니다</Text>. 20~60분이 적당합니다.{'\n'}
                    · <Text style={{ fontWeight: '700' }}>W1 / W2</Text> — 두 전극의 가중치입니다. 한쪽만 젖는 분은 이 비율을 바꿉니다.
                </Text>
            </View>

            {!!msg && (
                <View style={{ marginTop: s(10), padding: s(10), borderRadius: s(8),
                               backgroundColor: S.accent + '14', borderWidth: 1, borderColor: S.accent + '44' }}>
                    <Text style={{ fontSize: s(12.5), color: T.text }}>{msg}</Text>
                </View>
            )}

            {last?.CALC_TIME && (
                <Text style={{ fontSize: s(11.5), color: T.muted, marginTop: s(8) }}>
                    마지막 계산 {String(last.CALC_TIME).replace('T', ' ').slice(0, 16)}
                    {'  ·  '}기간 {last.DAYS}일 / 검증 {last.TESTD}일 / 리드 상한 {last.LEAD_MAX}분
                </Text>
            )}

            {/* 코드 기본값 */}
            <Section title="기저귀 코드 기본값"
                     desc="그 코드를 쓰는 센서 전체가 같이 바뀝니다. 사람마다 필요한 값이 비슷할 때 효율적입니다.">
                <View style={{ flexDirection: 'row', paddingHorizontal: s(12), paddingVertical: s(9),
                               backgroundColor: T.cardInner, gap: s(8) }}>
                    <Th w={s(26)}> </Th><Th w={s(64)}>코드</Th><Th w={s(74)}>적용 대상</Th>
                    <Th w={s(112)}>DIV</Th><Th w={s(74)}>사이클</Th><Th w={s(120)}>미알림</Th>
                    <Th w={s(200)}>개인 권장값 분포</Th>
                </View>
                {codes.length === 0 && (
                    <Text style={{ padding: s(18), fontSize: s(12.5), color: T.muted }}>
                        아직 계산한 추천이 없습니다. 위에서 「계산 시작」을 눌러 주세요.
                    </Text>
                )}
                {codes.map(r => {
                    const k = keyOf(r), applied = r.STATUS === 'applied', g = gain(r);
                    const sp = spreadOf(r.TARGET);
                    const wide = sp && sp.ratio >= 1.6;
                    const n = sensors.filter(x => x.DIAPER_CODE === r.TARGET).length;
                    return (
                        <View key={k} style={{ borderTopWidth: 1, borderTopColor: T.border,
                                               opacity: applied ? 0.55 : 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8),
                                           paddingHorizontal: s(12), paddingTop: s(10) }}>
                                <View style={{ width: s(26) }}>
                                    {applied ? <Text style={{ fontSize: s(11), color: S.accent }}>적용</Text>
                                             : <Check on={!!sel[k]} onPress={() => setSel({ ...sel, [k]: !sel[k] })} />}
                                </View>
                                <Td w={s(64)} bold>{r.TARGET}</Td>
                                <Td w={s(74)} color={T.textSub}>센서 {n}대</Td>
                                <Td w={s(112)}>{r.CUR_DIV} → <Text style={{ fontWeight: '700', color: S.accent }}>{r.NEW_DIV}</Text></Td>
                                <Td w={s(74)} color={T.muted}>{r.CYCLES}</Td>
                                <Td w={s(120)} color={g > 0 ? S.ok : T.textSub}>
                                    {r.MISS_CUR} → {r.MISS_NEW}{g > 0 ? `  (${g}↓)` : ''}
                                </Td>
                                <View style={{ width: s(200), flexDirection: 'row', alignItems: 'center', gap: s(6) }}>
                                    {sp ? <>
                                        <Text style={{ fontSize: s(12), color: T.text }}>{sp.lo} ~ {sp.hi}</Text>
                                        {wide && <Badge v={{ label: '편차 큼', color: S.warn }} />}
                                    </> : <Text style={{ fontSize: s(12), color: T.muted }}>—</Text>}
                                </View>
                            </View>
                            <Text style={{ paddingHorizontal: s(12), paddingLeft: s(46), paddingBottom: s(10),
                                           paddingTop: s(3), fontSize: s(11.5), color: T.muted, lineHeight: s(17) }}>
                                {wide
                                    ? `이 코드를 쓰는 ${sp!.n}명이 서로 ${sp!.ratio}배까지 다른 값을 필요로 합니다. 코드 하나로는 맞추기 어려우니 아래 개인 추천을 쓰는 편이 낫습니다.`
                                    : (g > 0
                                        ? `센서 ${n}대가 함께 바뀝니다. 놓친 알림이 ${g}번 줄어듭니다.`
                                        : `센서 ${n}대가 함께 바뀌지만 놓친 알림은 줄지 않습니다.`)}
                            </Text>
                        </View>
                    );
                })}
            </Section>

            {/* 개인 */}
            <Section title="개인 추천"
                     desc="그 사람만 따로 떼어내 값을 줍니다. 적용하면 전용 기저귀 코드가 새로 생기고, 원래 코드는 그대로 남습니다.">
                <View style={{ flexDirection: 'row', paddingHorizontal: s(12), paddingVertical: s(9),
                               backgroundColor: T.cardInner, gap: s(8) }}>
                    <Th w={s(26)}> </Th><Th w={s(76)}>이름</Th><Th w={s(52)}>코드</Th>
                    <Th w={s(104)}>DIV</Th><Th w={s(132)}>W1 / W2</Th>
                    <Th w={s(118)}>미알림</Th><Th w={s(104)}>알림 리드</Th><Th w={s(78)}>판단</Th>
                </View>
                {sensors.length === 0 && (
                    <Text style={{ padding: s(18), fontSize: s(12.5), color: T.muted }}>
                        개인 조정이 필요한 센서가 없습니다.
                    </Text>
                )}
                {sensors.map(r => {
                    const k = keyOf(r), applied = r.STATUS === 'applied', g = gain(r);
                    const wch = Number(r.NEW_W1) !== Number(r.CUR_W1);
                    const v = verdict(r);
                    return (
                        <View key={k} style={{ borderTopWidth: 1, borderTopColor: T.border,
                                               opacity: applied ? 0.55 : 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8),
                                           paddingHorizontal: s(12), paddingTop: s(10) }}>
                                <View style={{ width: s(26) }}>
                                    {applied ? <Text style={{ fontSize: s(11), color: S.accent }}>적용</Text>
                                             : <Check on={!!sel[k]} onPress={() => setSel({ ...sel, [k]: !sel[k] })} />}
                                </View>
                                <Td w={s(76)} bold>{r.NAME || r.TARGET}</Td>
                                <Td w={s(52)} color={T.muted}>{r.DIAPER_CODE}</Td>
                                <Td w={s(104)}>{r.CUR_DIV} → <Text style={{ fontWeight: '700', color: S.accent }}>{r.NEW_DIV}</Text></Td>
                                <Td w={s(132)} color={wch ? T.text : T.muted}>
                                    {wch ? `${r.CUR_W1}/${r.CUR_W2} → ${r.NEW_W1}/${r.NEW_W2}` : '그대로'}
                                </Td>
                                <Td w={s(118)} color={g > 0 ? S.ok : T.textSub}>
                                    {r.MISS_CUR} → {r.MISS_NEW} / {r.CYCLES}
                                </Td>
                                <Td w={s(104)} color={v.k === 'bad' || v.k === 'warn' ? S.warn : T.muted}>
                                    {r.LEAD_CUR == null ? '—' : `${r.LEAD_CUR}분`} → {r.LEAD_NEW == null ? '—' : `${r.LEAD_NEW}분`}
                                </Td>
                                <View style={{ width: s(78) }}><Badge v={v} /></View>
                            </View>
                            <Text style={{ paddingLeft: s(46), paddingRight: s(12), paddingBottom: s(10),
                                           paddingTop: s(3), fontSize: s(11.5), color: T.muted, lineHeight: s(17) }}>
                                {v.why}
                            </Text>
                        </View>
                    );
                })}
            </Section>

            <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10), marginTop: s(14) }}>
                <Btn label="「권함」만 고르기" kind="sub" onPress={() => {
                    const n: Record<string, boolean> = {};
                    rows.forEach(r => { if (r.STATUS !== 'applied' && verdict(r).k === 'good') n[keyOf(r)] = true; });
                    setSel(n);
                }} />
                <Btn label={`선택한 ${picked.length}건 적용`} onPress={apply} disabled={busy || !picked.length} />
                <Text style={{ fontSize: s(11.5), color: T.muted }}>
                    계산식은 서버(gwserver)에서 돕니다. 값만 바꾸면 30초 안에 반영되고 중계기·센서는 건드리지 않습니다.
                </Text>
            </View>

            {/* 이력 */}
            <Section title="변경 이력" desc="적용 전 값을 그대로 보관합니다. 언제든 되돌릴 수 있습니다.">
                {hist.length === 0 && (
                    <Text style={{ padding: s(18), fontSize: s(12.5), color: T.muted }}>아직 적용한 적이 없습니다.</Text>
                )}
                {hist.map(h => (
                    <View key={h.IDX} style={{ flexDirection: 'row', alignItems: 'center', gap: s(10),
                                               paddingHorizontal: s(12), paddingVertical: s(10),
                                               borderTopWidth: 1, borderTopColor: T.border,
                                               opacity: h.REVERTED_AT ? 0.5 : 1 }}>
                        <Td w={s(130)}>{String(h.APPLIED_AT || '').replace('T', ' ').slice(5, 16)}</Td>
                        <Td w={s(60)}>{h.KIND === 'code' ? '코드' : '개인'}</Td>
                        <Td w={s(110)} bold>{h.NAME || h.TARGET}</Td>
                        <Td w={s(260)} color={T.textSub}>{h.NOTE}</Td>
                        <View style={{ width: s(110) }}>
                            {h.REVERTED_AT
                                ? <Text style={{ fontSize: s(11.5), color: T.muted }}>되돌림</Text>
                                : <Btn label="되돌리기" kind="sub" onPress={() => revert(h)} disabled={busy} />}
                        </View>
                    </View>
                ))}
            </Section>
        </ScrollView>
    );
};

export default SensorTune;
